<script setup lang="ts">
    import { Button } from '@/components/ui/button';
    import {
        Dialog,
        DialogContent,
        DialogHeader,
        DialogTitle,
        DialogClose,
        DialogFooter,
        DialogDescription,
        DialogBody,
    } from '@/components/ui/dialog';
    import { computed, ref } from 'vue';
    import { useI18n } from 'vue-i18n';
    import type { Payslip } from '@/types';
    import { toast } from 'vue-sonner';
    import { useForm } from '@inertiajs/vue3';
    import { InputRow } from '@/components/ui/form';
    import { Textarea } from '@/components/ui/textarea';
    import { useDateFormat } from '@/composables/useDateFormat';

    import QueryKey from '@/enums/QueryKey';
    import { useQueryClient } from '@tanstack/vue-query';

    const { formatDMYFromIso } = useDateFormat();

    const queryClient = useQueryClient();

    const props = defineProps<{
        payslip: Payslip;
        modelValue?: boolean;
        selectedCompanyId: number;
    }>();

    const emit = defineEmits<{
        'update:modelValue': [value: boolean];
    }>();

    const open = computed({
        get: () => props.modelValue ?? false,
        set: (value) => emit('update:modelValue', value),
    });

    const { t } = useI18n();

    const form = useForm({
        paySlips: [props.payslip.id],
        comment: '',
    });

    const sendReminder = () => {
        form.post(route('v2.salary.payslip.reminder', { company: props.selectedCompanyId }), {
            preserveScroll: true,
            onSuccess: () => {
                form.resetAndClearErrors();
                toast.success(t('mails.success'));
                queryClient.invalidateQueries({
                    queryKey: [QueryKey.PAYROLLS, props.selectedCompanyId],
                });
                handleClose();
            },
            onError: (error: any) => {
                if (error instanceof Error) {
                    toast.error(t('forms.system_error'));
                }
            },
        });
    };

    const handleClose = () => {
        open.value = false;
    };
</script>

<template>
    <Dialog
        :open="open"
        @update:open="(value) => (open = value)"
    >
        <DialogContent class="sm:max-w-lg">
            <form
                class="flex max-h-full flex-col overflow-hidden"
                @submit.prevent="sendReminder"
            >
                <DialogHeader>
                    <DialogTitle>
                        {{ t('pay-slips.confirm.title') }}
                    </DialogTitle>
                </DialogHeader>

                <DialogBody class="space-y-5">
                    <DialogDescription class="space-y-2">
                        <p>
                            {{ t('pay-slips.confirm.involved') }}
                            <span class="font-bold">
                                {{ payslip.payroll.month }}/{{ payslip.payroll.year }}
                            </span>
                        </p>
                        <p v-if="payslip.last_reminded_at">
                            {{ t('pay-slips.confirm.last_reminded_at') }}
                            <span class="font-bold">
                                {{ formatDMYFromIso(payslip.last_reminded_at) }}
                            </span>
                        </p>
                    </DialogDescription>

                    <InputRow
                        :form="form"
                        name="comment"
                        label="pay-slips.comment"
                    >
                        <Textarea
                            id="comment"
                            v-model="form.comment"
                            :placeholder="$t('pay-slips.confirm.comment')"
                        />
                    </InputRow>
                </DialogBody>

                <DialogFooter class="gap-2 border-t sm:justify-center">
                    <DialogClose as-child>
                        <Button
                            variant="outline"
                            size="sm"
                            @click="handleClose"
                        >
                            {{ t('forms.close') }}
                        </Button>
                    </DialogClose>

                    <Button
                        :disabled="form.processing"
                        :loading="form.processing"
                        size="sm"
                    >
                        {{ t('pay-slips.reminder-text') }}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    </Dialog>
</template>
